import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, RuleDraw, Reveal, EASE } from "../components/Reveal";
import DevelopmentLoop from "../components/DevelopmentLoop";
import SectionNav from "../components/SectionNav";
import { SectionHead, Stat, CtaBand } from "../components/ui";
import { IMAGES, PLATFORM_STAGES, WHY_CARDS, PROJECTS, FID_NOTE, PARTNERS } from "../data/site";

const HOME_SECTIONS = [
    { id: "platform", label: "Platform" },
    { id: "why-addigen", label: "Why AddiGen" },
    { id: "pci", label: "PCIs" },
    { id: "markets", label: "Markets" },
    { id: "technologies", label: "Technologies" },
    { id: "partners", label: "Partners" },
];

const STATS = [
    { value: "02", label: "EU-designated Projects of Common Interest" },
    { value: "02", label: "European markets — Germany & Denmark" },
    { value: "04", label: "Storage developments in progress" },
    { value: "02", label: "Complementary technologies — CAES & BESS" },
];

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    return (
        <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-navy" data-testid="home-hero">
            <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
                <img
                    src={IMAGES.hero}
                    alt="High-voltage substation at night"
                    className="img-grade h-full w-full object-cover opacity-55"
                    loading="eager"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/20" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy to-transparent" />
            <div className="container-x relative pb-20 pt-40 sm:pb-24">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="eyebrow-light mb-6"
                >
                    Long-duration energy storage — Europe
                </motion.p>
                <MaskedLines
                    lines={["Europe's long-duration", "energy storage developer."]}
                    className="h1-display max-w-5xl text-white"
                    lineClassName="text-white"
                />
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                    className="mt-8 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
                >
                    Developing large-scale CAES and BESS projects to support Europe's
                    decarbonisation and renewable energy targets.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                    className="mt-10 flex flex-wrap gap-4"
                >
                    <Link to="/projects" className="btn-solid-light" data-testid="hero-cta-projects">
                        Explore Projects
                    </Link>
                    <Link to="/company/difference" className="btn-outline-light" data-testid="hero-cta-why">
                        Why AddiGen
                    </Link>
                </motion.div>
            </div>
            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 1 }}
                className="absolute bottom-10 right-6 hidden items-center gap-4 lg:flex"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/50">Scroll</span>
                <span className="block h-px w-16 bg-white/30" />
            </motion.div>
        </section>
    );
}

function StatBand() {
    return (
        <section className="border-b border-line bg-paper">
            <div className="container-x grid grid-cols-2 gap-x-8 gap-y-10 py-14 sm:py-16 lg:grid-cols-4">
                {STATS.map((s, i) => (
                    <Reveal key={s.label} delay={i * 0.08}>
                        <Stat value={s.value} label={s.label} size="sm" />
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function Positioning() {
    return (
        <section className="bg-paper">
            <div className="container-x grid gap-10 py-20 sm:py-24 lg:grid-cols-12 lg:py-32">
                <div className="lg:col-span-3">
                    <Reveal>
                        <p className="eyebrow lg:sticky lg:top-36">Positioning</p>
                    </Reveal>
                </div>
                <div className="lg:col-span-9">
                    <Reveal>
                        <p className="font-display text-2xl font-medium leading-[1.3] tracking-[-0.01em] text-ink sm:text-3xl lg:text-[2.6rem] lg:leading-[1.25]">
                            AddiGen develops large-scale CAES and BESS projects to support
                            Europe's decarbonisation and renewable energy targets — building
                            toward long-term ownership and operation of energy storage
                            infrastructure.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="body-lg mt-8 max-w-2xl">
                            AddiGen is an infrastructure developer, not a technology vendor. We
                            understand the energy system, we develop complex infrastructure, and
                            we are building a long-term platform — disciplined, system-led and
                            European.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-10">
                            <Link to="/company/story" className="link-underline" data-testid="positioning-story-link">
                                Our story
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function Platform() {
    return (
        <section id="platform" className="bg-navy text-white">
            <div className="container-x py-20 sm:py-24 lg:py-32">
                <SectionHead
                    dark
                    eyebrow="Development platform"
                    title="From early-stage opportunity to long-term infrastructure — and back again."
                    intro="AddiGen operates a repeatable development platform. Projects progress through a disciplined cycle, and realised capital returns to originate the next generation."
                />
                <div className="mt-16 sm:mt-20">
                    <DevelopmentLoop stages={PLATFORM_STAGES} />
                </div>
            </div>
        </section>
    );
}

function WhyAddigen() {
    return (
        <section id="why-addigen" className="bg-paper">
            <div className="container-x py-20 sm:py-24 lg:py-32">
                <SectionHead
                    eyebrow="Why AddiGen"
                    title="Institutional principles, applied to energy storage development."
                />
                <div className="mt-14 grid gap-x-8 gap-y-12 sm:grid-cols-2 xl:grid-cols-4">
                    {WHY_CARDS.map((c, i) => (
                        <Reveal key={c.n} delay={i * 0.08}>
                            <article className="flex h-full flex-col border-t-2 border-ink pt-6" data-testid={`why-card-${i + 1}`}>
                                <span className="font-mono text-[11px] tracking-[0.2em] text-blue">{c.n}</span>
                                <h3 className="h3-compact mt-5">{c.title}</h3>
                                <p className="body-sm mt-4">{c.body}</p>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function PciBlock() {
    return (
        <section id="pci" className="bg-blue text-white" data-testid="pci-block">
            <div className="container-x py-16 sm:py-20 lg:py-24">
                <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60">EU Projects of Common Interest</p>
                            <p className="mt-6 font-display text-6xl font-bold tracking-[-0.03em] sm:text-7xl">2</p>
                            <p className="mt-4 max-w-sm font-display text-2xl font-semibold leading-snug sm:text-3xl">
                                Currently developing 2 EU-designated Projects of Common Interest.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <ul className="divide-y divide-white/25 border-y border-white/25">
                                {PROJECTS.filter((p) => p.pci).map((p) => (
                                    <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-3 py-6">
                                        <div className="flex items-baseline gap-5">
                                            <span className="font-mono text-[12px] uppercase tracking-[0.18em] text-white/70">{p.pci}</span>
                                            <span className="font-display text-lg font-semibold sm:text-xl">{p.name}</span>
                                        </div>
                                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">{p.market}</span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                href="https://single-market-economy.ec.europa.eu/sectors/infrastructure/projects-common-interest_en"
                                target="_blank"
                                rel="noreferrer"
                                className="mt-8 inline-flex items-center gap-3 font-display text-[13px] font-semibold uppercase tracking-[0.14em] text-white"
                                data-testid="pci-learn-more-link"
                            >
                                Learn more about EU Projects of Common Interest
                                <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true"><path d="M1 11 11 1M4 1h7v7" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>
                            </a>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Markets() {
    return (
        <section id="markets" className="bg-paper">
            <div className="container-x py-20 sm:py-24 lg:py-32">
                <SectionHead
                    eyebrow="Projects"
                    title="Two markets. Four storage developments."
                />
                <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-8">
                    {["Germany", "Denmark"].map((country, i) => (
                        <Reveal key={country} delay={i * 0.1}>
                            <article className="flex h-full flex-col border border-line bg-white" data-testid={`market-card-${country.toLowerCase()}`}>
                                <Link to="/projects" className="group relative block aspect-[16/7] overflow-hidden">
                                    <img
                                        src={country === "Germany" ? IMAGES.towersDusk : IMAGES.towersGreen}
                                        alt={`Energy infrastructure in ${country}`}
                                        loading="lazy"
                                        className="img-grade h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                                    />
                                </Link>
                                <div className="flex flex-1 flex-col p-8 sm:p-10">
                                    <h3 className="font-display text-3xl font-bold tracking-[-0.01em]">{country}</h3>
                                    <ul className="mt-8 flex-1 space-y-0 divide-y divide-line border-t border-line">
                                        {PROJECTS.filter((p) => p.market === country).map((p) => (
                                            <li key={p.id}>
                                                <Link to={`/projects/${p.slug}`} className="group flex items-baseline justify-between gap-4 py-4" data-testid={`project-card-${p.id}`}>
                                                    <span>
                                                        <span className="font-display text-base font-semibold transition-colors group-hover:text-blue">{p.name}</span>
                                                        <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-ink-3">
                                                            {p.tech}
                                                            {p.pci ? ` · ${p.pci}` : ""} · {p.stats.map((s) => `${s.value} ${s.unit}`).join(" / ")}
                                                        </span>
                                                    </span>
                                                    <span aria-hidden="true" className="font-mono text-xs text-ink-3 transition-transform group-hover:translate-x-1">→</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-ink-3">{FID_NOTE}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <div className="mt-10">
                        <Link to="/projects" className="link-underline">All projects</Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

function TechStrip() {
    return (
        <section id="technologies" className="border-y border-line bg-white">
            <div className="container-x grid gap-px lg:grid-cols-2">
                {[
                    { t: "CAES", d: "Long-duration storage with multi-hour to multi-day capability and system resilience.", c: "Compressed Air Energy Storage" },
                    { t: "BESS", d: "Shorter-duration flexibility, fast response and grid services.", c: "Battery Energy Storage Systems" },
                ].map((x, i) => (
                    <Reveal key={x.t} delay={i * 0.08} className={i === 0 ? "lg:border-r lg:border-line" : ""}>
                        <div className="flex h-full flex-col justify-between gap-8 py-12 lg:px-14 lg:py-16">
                            <div>
                                <p className="eyebrow">{x.c}</p>
                                <p className="stat-numeral mt-4 text-5xl">{x.t}</p>
                                <p className="body-lg mt-4 max-w-sm">{x.d}</p>
                            </div>
                            <Link to="/technology" className="link-underline" data-testid={`tech-link-${x.t.toLowerCase()}`}>
                                Technology
                            </Link>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

function PartnerStrip() {
    return (
        <section id="partners" className="bg-paper">
            <div className="container-x py-14 sm:py-16">
                <Reveal>
                    <p className="eyebrow mb-8 text-center lg:text-left">Partners & stakeholders</p>
                    <ul className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 lg:justify-between">
                        {PARTNERS.map((p) => (
                            <li key={p} className="font-display text-lg font-semibold uppercase tracking-[0.14em] text-ink-3/80">
                                {p}
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <SectionNav items={HOME_SECTIONS} navKey="home" />
            <StatBand />
            <Positioning />
            <Platform />
            <WhyAddigen />
            <PciBlock />
            <Markets />
            <TechStrip />
            <PartnerStrip />
            <CtaBand />
        </>
    );
}
